import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Button,
  Paper,
  Grid,
  Tabs,
  Tab,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Preview as PreviewIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Router as RouterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';
import api from '../services/api';

// Importando componentes refatorados
import { ImpressoraConfig, TicketPreview } from '../components/painel-config/impressora';
import PrinterBridgeConfig from '../components/painel-config/printer-bridge';
import ConfiguracoesFooter from '../components/painel-config/ConfiguracoesFooter';
import NavigationBreadcrumbs from '../components/painel-config/Breadcrumbs';

const ConfiguracaoTickets = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode } = useTheme();
  const baseTheme = useMuiTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testeEnviado, setTesteEnviado] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [htmlDialogOpen, setHtmlDialogOpen] = useState(false);
  const [templatesDisponiveis, setTemplatesDisponiveis] = useState([]);
  const [alerta, setAlerta] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  // Estado para configurações da impressora
  const [configuracoes, setConfiguracoes] = useState({
    // Informações do estabelecimento
    nomeEmpresa: 'NOME DO CARTÓRIO',
    enderecoEmpresa: 'Rua Exemplo, 123 - Centro',
    siteEmpresa: 'www.cartoriocriciuma.com.br',
    telefoneEmpresa: '(11) 1234-5678',
    
    // Opções de impressão
    incluirLogo: true,
    incluirQrCode: true,
    incluirData: true,
    incluirHora: true,
    mensagemExtra: 'Agradecemos a preferência!',
    qrCodeUrl: 'https://www.cartoriocriciuma.com.br',
    
    // Nome do template de ticket (arquivo .json em src/printer/templates)
    templateTicket: '',
    
    // Textos customizáveis do template
    textoPreferencial: 'ATENDIMENTO PREFERENCIAL',
    textoSenha: 'SENHA',
    instrucao1: 'Aguarde o chamado da sua senha',
    instrucao2: 'no painel eletrônico',
    textoQrCode1: 'Escaneie o QR Code',
    textoQrCode2: 'para acessar nosso site'
  });

  // Dados de exemplo para preview
  const [previewData, setPreviewData] = useState({
    senha: 'A123',
    servicoNome: 'ATENDIMENTO GERAL',
    preferencial: true,
    data: new Date().toLocaleDateString('pt-BR'),
    hora: new Date().toLocaleTimeString('pt-BR'),
    logoPath: 'https://via.placeholder.com/150x80?text=LOGO',
    qrCodeImage: 'https://via.placeholder.com/100x100?text=QR'
  });

  // Criando um tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
    },
  });

  // (removido) editor HTML não será mais usado para templateTicket

  // Carregar configurações atuais
  useEffect(() => {
    const carregarConfiguracoesETemplates = async () => {
      try {
        setLoading(true);
        const [respConfigs, respTemplates] = await Promise.all([
          api.get('/printer/configuracao'),
          api.get('/printer/templates')
        ]);

        if (respTemplates.data && respTemplates.data.success) {
          setTemplatesDisponiveis(respTemplates.data.data || []);
        }

        if (respConfigs.data && respConfigs.data.success) {
          const configsCarregadas = respConfigs.data.data;
          setConfiguracoes(configsCarregadas);
          setPreviewData(prev => ({
            ...prev,
            nomeEmpresa: configsCarregadas.nomeEmpresa,
            enderecoEmpresa: configsCarregadas.enderecoEmpresa,
            telefoneEmpresa: configsCarregadas.telefoneEmpresa,
            siteEmpresa: configsCarregadas.siteEmpresa,
            mensagemExtra: configsCarregadas.mensagemExtra,
            incluirLogo: configsCarregadas.incluirLogo,
            incluirQrCode: configsCarregadas.incluirQrCode,
            incluirData: configsCarregadas.incluirData,
            incluirHora: configsCarregadas.incluirHora
          }));
        }
      } catch (error) {
        console.error('Erro ao carregar configurações/templates:', error);
        setAlerta({
          open: true,
          message: 'Não foi possível carregar configurações/templates',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    carregarConfiguracoesETemplates();
  }, []);

  // Função para alternar a sidebar
  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  // Função para alterar tab
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Função para atualizar os campos do formulário
  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setConfiguracoes(prevState => ({
      ...prevState,
      [name]: newValue
    }));

    // Atualizar preview data se necessário
    if (['nomeEmpresa', 'enderecoEmpresa', 'telefoneEmpresa', 'siteEmpresa', 'mensagemExtra', 'incluirLogo', 'incluirQrCode', 'incluirData', 'incluirHora'].includes(name)) {
      setPreviewData(prev => ({
        ...prev,
        [name]: newValue
      }));
    }
  };

  // Função para salvar as configurações
  const salvarConfiguracoes = async () => {
    try {
      setSalvando(true);
      
      const response = await api.post('/printer/configuracao', configuracoes);
      
      if (response.data && response.data.success) {
        setAlerta({
          open: true,
          message: 'Configurações salvas com sucesso!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setAlerta({
        open: true,
        message: 'Erro ao salvar configurações. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setSalvando(false);
    }
  };

  // (removido) salvar template HTML – não aplicável no modo por arquivo JSON

  // Função para imprimir teste
  const imprimirTeste = async () => {
    try {
      setSalvando(true);
      
      // Salvar primeiro
      await api.post('/printer/configuracao', configuracoes);
      
      // Imprimir teste
      const response = await api.post('/printer/teste');
      
      if (response.data && response.data.success) {
        setAlerta({
          open: true,
          message: 'Teste de impressão enviado com sucesso!',
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Erro ao imprimir teste:', error);
      setAlerta({
        open: true,
        message: 'Erro ao enviar teste de impressão. Verifique a impressora.',
        severity: 'error'
      });
    } finally {
      setSalvando(false);
    }
  };

  // Função para resetar para template padrão
  const resetarTemplate = () => {
    setConfiguracoes(prev => ({
      ...prev,
      templateTicket: ''
    }));
    setAlerta({
      open: true,
      message: 'Template definido como Padrão (interno)',
      severity: 'info'
    });
  };

  // Função para exportar template
  const exportarTemplate = () => {
    const dataStr = JSON.stringify(configuracoes, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `template-ticket-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Função para importar template
  const importarTemplate = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedConfig = JSON.parse(e.target.result);
          setConfiguracoes(importedConfig);
          setAlerta({
            open: true,
            message: 'Template importado com sucesso!',
            severity: 'success'
          });
        } catch (error) {
          setAlerta({
            open: true,
            message: 'Erro ao importar template. Arquivo inválido.',
            severity: 'error'
          });
        }
      };
      reader.readAsText(file);
    }
  };

  // Fechar alerta
  const fecharAlerta = () => {
    setAlerta({ ...alerta, open: false });
  };

  // Função para exibir alertas (callback para componentes filhos)
  const handleAlert = (alertData) => {
    setAlerta({
      open: true,
      message: alertData.message,
      severity: alertData.severity || 'info'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        customTheme={customTheme}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
          handleSidebarToggle={handleSidebarToggle}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4, flex: 1 }}>
          <NavigationBreadcrumbs
            items={[
              { label: 'Dashboard', link: '/dashboard' },
              { label: 'Configurações', link: '/configuracoes' },
              { label: 'Configuração de Tickets' }
            ]}
          />

          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1">
                Configuração de Tickets de Senha
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1 }}>
                <input
                  accept=".json"
                  style={{ display: 'none' }}
                  id="import-template"
                  type="file"
                  onChange={importarTemplate}
                />
                <label htmlFor="import-template">
                  <Tooltip title="Importar Template">
                    <IconButton component="span" color="primary">
                      <UploadIcon />
                    </IconButton>
                  </Tooltip>
                </label>
                
                <Tooltip title="Exportar Template">
                  <IconButton onClick={exportarTemplate} color="primary">
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Resetar Template">
                  <IconButton onClick={resetarTemplate} color="warning">
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={imprimirTeste}
                  disabled={salvando}
                >
                  Teste de Impressão
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={salvarConfiguracoes}
                  disabled={salvando}
                >
                  {salvando ? 'Salvando...' : 'Salvar'}
                </Button>
              </Box>
            </Box>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Configure e personalize completamente os tickets de senha impressos pelo sistema.
            </Typography>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab 
                  icon={<SettingsIcon />} 
                  label="Configurações Básicas" 
                  sx={{ minHeight: 48 }}
                />
                <Tab 
                  icon={<PreviewIcon />} 
                  label="Visualização" 
                  sx={{ minHeight: 48 }}
                />
                <Tab 
                  icon={<RouterIcon />} 
                  label="Printer Bridge" 
                  sx={{ minHeight: 48 }}
                />
              </Tabs>
            </Box>

            {/* Tab 1: Configurações Básicas */}
            {tabValue === 0 && (
              <ImpressoraConfig
                configuracoes={configuracoes}
                handleChange={handleChange}
                customTheme={customTheme}
                imprimirTeste={imprimirTeste}
                imprimirTesteQrCode={imprimirTeste}
                testeEnviado={testeEnviado}
                templateOptions={templatesDisponiveis}
              />
            )}

            {/* Tab 2: Visualização */}
            {tabValue === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Pré-visualização do Ticket
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Visualize como o ticket será impresso com as configurações atuais.
                  </Typography>
                </Grid>
                
                <Grid item xs={12} md={8}>
                  <TicketPreview
                    configuracoes={configuracoes}
                    dadosExemplo={{...previewData, ...configuracoes}}
                    customTheme={customTheme}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Dados de Exemplo
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Senha:</strong> {previewData.senha}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Serviço:</strong> {previewData.servicoNome}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Preferencial:</strong> {previewData.preferencial ? 'Sim' : 'Não'}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Data:</strong> {previewData.data}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Hora:</strong> {previewData.hora}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Tab 3: Printer Bridge */}
            {tabValue === 2 && (
              <Box>
                <PrinterBridgeConfig
                  customTheme={customTheme}
                  onAlert={handleAlert}
                />
              </Box>
            )}
          </Paper>

          <ConfiguracoesFooter 
            onSave={salvarConfiguracoes}
            loading={salvando}
            showAdvancedButton={false}
          />
        </Container>

        {/* Dialog para visualizar HTML */}
        <Dialog
          open={htmlDialogOpen}
          onClose={() => setHtmlDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Código HTML do Template</DialogTitle>
          <DialogContent>
            <pre style={{ 
              whiteSpace: 'pre-wrap', 
              fontFamily: 'monospace', 
              fontSize: '12px',
              backgroundColor: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto'
            }}>
              {configuracoes.templateTicket}
            </pre>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHtmlDialogOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para alertas */}
        <Snackbar 
          open={alerta.open} 
          autoHideDuration={6000} 
          onClose={fecharAlerta}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={fecharAlerta} 
            severity={alerta.severity}
            variant="filled"
          >
            {alerta.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default ConfiguracaoTickets; 